import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { EnvTest } from "@/components/EnvTest";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppSidebar from "@/components/AppSidebar";
import { CashpurrFAB } from "@/components/ui/cashpurr-fab";
import { BadgeNotificationProvider } from "@/components/ui/badge-notification";
import Home from "./pages/Home";
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Careers from "./pages/Careers";
import CookiePolicy from "./pages/CookiePolicy";
import GDPR from "./pages/GDPR";
import HelpCenter from "./pages/HelpCenter";
import FAQ from "./pages/FAQ";
import Community from "./pages/Community";
import Status from "./pages/Status";
import Docs from "./pages/Docs";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/app/Dashboard";
import IdeaValidation from "./pages/app/IdeaValidation";
import ValidationResults from "./pages/app/ValidationResults";
import BossMode from "./pages/app/BossMode";
import Playbooks from "./pages/app/Playbooks";
import ROITracker from "./pages/app/ROITracker";
import Pods from "./pages/app/Pods";
import Settings from "./pages/app/Settings";
import FounderJournal from "./pages/app/FounderJournal";
import SocialTracker from "./pages/app/SocialTracker";
import PromptLibrary from "./pages/app/PromptLibrary";
import NotFound from "./pages/NotFound";
import NewDashboard from "./pages/app/NewDashboard";
import NewIdeaValidation from "./pages/app/NewIdeaValidation";
import IdeaAssessment from './components/idea-validation/IdeaAssessment';
import NewBossMode from "./pages/app/NewBossMode";
import NewROITracker from "./pages/app/NewROITracker";
import NewCommunityPods from "./pages/app/NewCommunityPods";
import NewFounderJournal from "./pages/app/NewFounderJournal";
import UsefulToolsPage from "./pages/app/UsefulTools";
import NewSettings from "./pages/app/NewSettings";
import MainLayout from "./components/layout/MainLayout";
import IdeaValidationGate from "./components/IdeaValidationGate";
import SubscriptionTest from "./pages/app/SubscriptionTest";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 60000,
    },
  },
});

const AppLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="flex h-screen">
    <AppSidebar />
    <main className="flex-1 overflow-auto">{children}</main>
    <CashpurrFAB />
  </div>
);

const PublicLayout = ({ children }: { children: React.ReactNode }) => (
  <>
    {children}
    <Footer />
  </>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <BrowserRouter 
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <BadgeNotificationProvider>
            <EnvTest />
            <Toaster />
            <Sonner />
            <Navigation />
            <Routes>
              {/* Public Marketing Pages */}
              <Route path="/" element={
                <PublicLayout>
                  <Home />
                </PublicLayout>
              } />
              <Route path="/features" element={
                <PublicLayout>
                  <Features />
                </PublicLayout>
              } />
              <Route path="/pricing" element={
                <PublicLayout>
                  <Pricing />
                </PublicLayout>
              } />
              <Route path="/about" element={
                <PublicLayout>
                  <About />
                </PublicLayout>
              } />
              <Route path="/contact" element={
                <PublicLayout>
                  <Contact />
                </PublicLayout>
              } />
              <Route path="/privacy" element={
                <PublicLayout>
                  <PrivacyPolicy />
                </PublicLayout>
              } />
              <Route path="/terms" element={
                <PublicLayout>
                  <TermsOfService />
                </PublicLayout>
              } />
              <Route path="/careers" element={
                <PublicLayout>
                  <Careers />
                </PublicLayout>
              } />
              <Route path="/cookies" element={
                <PublicLayout>
                  <CookiePolicy />
                </PublicLayout>
              } />
              <Route path="/gdpr" element={
                <PublicLayout>
                  <GDPR />
                </PublicLayout>
              } />
              <Route path="/help" element={
                <PublicLayout>
                  <HelpCenter />
                </PublicLayout>
              } />
              <Route path="/docs" element={
                <PublicLayout>
                  <Docs />
                </PublicLayout>
              } />
              <Route path="/faq" element={
                <PublicLayout>
                  <FAQ />
                </PublicLayout>
              } />
              <Route path="/community" element={
                <PublicLayout>
                  <Community />
                </PublicLayout>
              } />
              <Route path="/status" element={
                <PublicLayout>
                  <Status />
                </PublicLayout>
              } />
              
              {/* Auth Pages */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              
              {/* Main App Routes - Using New Comprehensive Components */}
              <Route path="/app" element={
                <ProtectedRoute>
                  <MainLayout>
                    <NewDashboard />
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/dashboard" element={
                <ProtectedRoute>
                  <MainLayout>
                    <NewDashboard />
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/idea-validation" element={
                <ProtectedRoute>
                  <MainLayout>
                    <IdeaAssessment />
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/boss-mode" element={
                <ProtectedRoute>
                  <MainLayout>
                    <IdeaValidationGate>
                      <NewBossMode />
                    </IdeaValidationGate>
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/boss" element={
                <ProtectedRoute>
                  <MainLayout>
                    <IdeaValidationGate>
                      <NewBossMode />
                    </IdeaValidationGate>
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/roi-tracker" element={
                <ProtectedRoute>
                  <MainLayout>
                    <IdeaValidationGate>
                      <NewROITracker />
                    </IdeaValidationGate>
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/roi" element={
                <ProtectedRoute>
                  <MainLayout>
                    <IdeaValidationGate>
                      <NewROITracker />
                    </IdeaValidationGate>
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/community-pods" element={
                <ProtectedRoute>
                  <MainLayout>
                    <IdeaValidationGate>
                      <NewCommunityPods />
                    </IdeaValidationGate>
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/pods" element={
                <ProtectedRoute>
                  <MainLayout>
                    <IdeaValidationGate>
                      <NewCommunityPods />
                    </IdeaValidationGate>
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/founder-journal" element={
                <ProtectedRoute>
                  <MainLayout>
                    <IdeaValidationGate>
                      <NewFounderJournal />
                    </IdeaValidationGate>
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/journal" element={
                <ProtectedRoute>
                  <MainLayout>
                    <IdeaValidationGate>
                      <NewFounderJournal />
                    </IdeaValidationGate>
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route
                path="/app/settings"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <NewSettings />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/subscription-test"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <SubscriptionTest />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route path="/app/social-tracker" element={
                <ProtectedRoute>
                  <MainLayout>
                    <IdeaValidationGate>
                      <SocialTracker />
                    </IdeaValidationGate>
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/tools" element={
                <ProtectedRoute>
                  <UsefulToolsPage />
                </ProtectedRoute>
              } />
              <Route path="/app/useful-tools" element={
                <ProtectedRoute>
                  <UsefulToolsPage />
                </ProtectedRoute>
              } />
              <Route path="/app/prompts" element={
                <ProtectedRoute>
                  <MainLayout>
                    <PromptLibrary />
                  </MainLayout>
                </ProtectedRoute>
              } />

              {/* Legacy Routes - Keep for backward compatibility */}
              <Route path="/app/old-dashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/idea-dashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <IdeaValidation />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/validation-results/:id" element={
                <ProtectedRoute>
                  <AppLayout>
                    <ValidationResults />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/playbooks" element={
                <ProtectedRoute>
                  <MainLayout>
                    <IdeaValidationGate>
                      <Playbooks />
                    </IdeaValidationGate>
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/social" element={
                <ProtectedRoute>
                  <AppLayout>
                    <SocialTracker />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/app/prompts" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PromptLibrary />
                  </AppLayout>
                </ProtectedRoute>
              } />
              
              {/* 404 Page */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BadgeNotificationProvider>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
